import { ExperiencesResponse, ILegalEntity } from '@cemex-core/cmx-api';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { createSelector } from '@ngrx/store';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import {
  getBusinessLine,
  getBusinessLinePage,
  getOrderBuilderState,
  getSelectedExperience,
  getShippingCondition,
} from '../../../reducers';
import { IBusinessLineState } from '../../business-line-selection/reducers/business-line-page.reducer';
import { BusinessLinesState } from '../../business-line-selection/reducers/business-line/business-lines.reducer';
import { ShippingConditionsState } from '../../business-line-selection/reducers/shipping-condition/shipping-conditions.reducer';
import { RecipeFormValues } from '../models/form-values';
import { IJobsiteState } from '../reducers/jobsite-reducer/jobsite.reducer';
import { IPODState } from '../reducers/point-of-delivery-reducer/point-of-delivery.reducer';
import { IProductsState } from '../reducers/products-reducer/products.reducer';

export interface IHolidaysAndNextWorkingDay {
  nextWorkingDay: Date;
  blackout?: {
    startTime?: string;
    endTime?: string;
    startDay?: string;
    endDay?: string;
  };
  cutOffTime: string;
  customer: ILegalEntity;
  holidays: Date[];
  experiences: {
    selectedJobsite: any;
    experience: ExperiencesResponse;
  };
  timeZone?: {
    links: {
      self: string;
    };
    timeZoneId: number;
    iANACode: string;
    windowsCode: string;
    timeOffset: string;
  };
}

/**
 * Exports location specification state
 */
export const getLocationSpecificationState = createSelector(getOrderBuilderState, (state) => {
  return state.locationSpecificationPage;
});

export const getFieldsForLoadingCatalogsWhitExperience = createSelector(
  getLegalEntity,
  getBusinessLine,
  getShippingCondition,
  getSelectedExperience,
  (
    customer: ILegalEntity,
    businessLineState: BusinessLinesState,
    shippingContitionState: ShippingConditionsState,
    state,
  ) => {
    return { customer, businessLineState, shippingContitionState, selectedExperience: state.experience };
  },
);

export const getAsyncValidatorsFormById = (formId) =>
  createSelector(getOrderBuilderState, (state) => {
    return _safe(() => state.locationSpecificationPage.asyncValidations.forms.find((x) => x.id === formId), null);
  });

export const getLocationSpecificationFormsState = createSelector(getOrderBuilderState, (state) => {
  return state.locationSpecificationPage.locationSpecificationFormState;
});

export const getProcessingFormsState = createSelector(getLocationSpecificationFormsState, (state) => {
  const isProcessingForms =
    state.isProcessingForms || state.forms.find((form) => form.loadingLoads === true) !== undefined;
  return isProcessingForms;
});

export const getBucketsToCancel = createSelector(getLocationSpecificationFormsState, (state) => {
  return state.bucketsToCancel;
});

export const getErrorsModalState = createSelector(getLocationSpecificationFormsState, (state) => {
  return { showErrorsModal: state.showErrorsModal, allHaveError: state.allHaveError };
});

export const getLocationSpecificationFormsStateById = (formId) =>
  createSelector(getOrderBuilderState, (state) => {
    return _safe(
      () => state.locationSpecificationPage.locationSpecificationFormState.forms.find((x) => x.id === formId),
      null,
    );
  });

export const getLocationSpecificationFormsStateAndValidatorsById = (formId) =>
  createSelector(
    getLocationSpecificationFormsStateById(formId),
    getAsyncValidatorsFormById(formId),
    (formState: any, asyncValidatorsForm: any) => {
      return { formState, asyncValidatorsForm };
    },
  );

export const getRecipeLocationSpecifications = createSelector(getLocationSpecificationState, (state) => {
  return state.locationSpecificationFormState.forms;
});

export const getRecipeLocationSpecificationsUpdateRequest = createSelector(getLocationSpecificationState, (state) => {
  return state.locationSpecificationFormState.forms[0];
});

export const getLocationSpecificationContactPersonFormConfig = createSelector(getOrderBuilderState, (state) => {
  return state.locationSpecificationPage.locationSpecificationFormState.contactPersonFormConfig;
});

export const getLocatioSpecificationsUnits = createSelector(getLocationSpecificationState, (state) => {
  return state.units;
});

export const getJobsiteState = createSelector(getLocationSpecificationState, (state) => {
  return state.jobsite;
});

export const getTopProductState = createSelector(getLocationSpecificationState, (state) => {
  return state.products;
});

export const getNextWorkingDay = createSelector(getLocationSpecificationState, (state) => {
  return state.nextWorkingDay;
});

export const getReceivingPerson = createSelector(getLocationSpecificationState, (state) => {
  return state.receivingPersons;
});

export const getHolidays = createSelector(getLocationSpecificationState, (state) => {
  return state.disabledDates;
});

export const getPODState = createSelector(getLocationSpecificationState, (state) => {
  return state.pointOfDelivery;
});

export const getDeliveryTimeWindows = createSelector(
  getLocationSpecificationState,
  (state) => state.deliveryTimeWindows,
);

export const getHolidaysAndNextWorkingDay = createSelector(
  getNextWorkingDay,
  getHolidays,
  getSelectedExperience,
  getLegalEntity,
  (nextWorkingDay, holidays, experience, customer: ILegalEntity): IHolidaysAndNextWorkingDay => {
    return {
      nextWorkingDay: nextWorkingDay.nextWorkingDay,
      blackout: nextWorkingDay.blackout,
      holidays: holidays.holidays,
      experiences: experience,
      cutOffTime: nextWorkingDay.cutOffTime,
      timeZone: nextWorkingDay.timezone,
      customer,
    };
  },
);

export const getPurchaseOrderValidationStateByFormId = (formId) =>
  createSelector(getOrderBuilderState, (state) =>
    state.locationSpecificationPage.asyncValidations.forms.find((x) => x.id === formId)
      ? state.locationSpecificationPage.asyncValidations.forms.find((x) => x.id === formId).purchaseOrderValidating
      : null,
  );

export const getPurchaseOrderValidStateByFormId = (formId) =>
  createSelector(getOrderBuilderState, (state) =>
    state.locationSpecificationPage.asyncValidations.forms.find((x) => x.id === formId)
      ? state.locationSpecificationPage.asyncValidations.forms.find((x) => x.id === formId).purchaseOrderValid
      : null,
  );

export const getStateForLoadProductsConfig = createSelector(
  getJobsiteState,
  getBusinessLinePage,
  getTopProductState,
  getLegalEntity,
  getRecipeLocationSpecifications,
  getReceivingPerson,
  (
    jobsite: IJobsiteState,
    businessLineState: IBusinessLineState,
    product: IProductsState,
    customer: ILegalEntity,
    previousFormState: RecipeFormValues[],
    receivingPerson: any,
  ) => {
    return {
      jobsite,
      businessLineState,
      product,
      customer,
      previousFormState,
      receivingPerson,
    };
  },
);

export const getStateForJobsiteConfig = createSelector(
  getJobsiteState,
  getBusinessLinePage,
  getLegalEntity,
  getRecipeLocationSpecifications,
  (
    jobsite: IJobsiteState,
    businessLineState: IBusinessLineState,
    customer: ILegalEntity,
    previousFormState: RecipeFormValues[],
  ) => {
    return {
      jobsite,
      businessLineState,
      customer,
      previousFormState,
    };
  },
);

export const getJobsitePOD = createSelector(
  getPODState,
  getJobsiteState,
  (pointOfDelivery: IPODState, jobsite: IJobsiteState) => {
    return { pointOfDelivery, jobsite };
  },
);

export const getStateForLoadProductsConfigAndValidators = (index) =>
  createSelector(
    getJobsitePOD,
    getBusinessLinePage,
    getTopProductState,
    getLegalEntity,
    getRecipeLocationSpecifications,
    getReceivingPerson,
    getPurchaseOrderValidationStateByFormId(index),
    getPurchaseOrderValidStateByFormId(index),
    (
      jobsitePOD: { pointOfDelivery: IPODState; jobsite: IJobsiteState },
      businessLineState: IBusinessLineState,
      product: IProductsState,
      customer: ILegalEntity,
      previousFormState: RecipeFormValues[],
      receivingPerson: any,
      purchaseOrderValidating: boolean,
      purchaseOrderValid: boolean,
    ) => {
      return {
        jobsitePOD,
        businessLineState,
        product,
        customer,
        previousFormState,
        receivingPerson,
        purchaseOrderValidating,
        purchaseOrderValid,
      };
    },
  );

export const getSelectedJobsiteSalesArea = createSelector(getJobsiteState, (state) => {
  return state.selectedSalesArea;
});

export const getPurchaseOrderValidation = createSelector(getSelectedJobsiteSalesArea, (selectedSalesArea) => {
  if (selectedSalesArea) {
    return selectedSalesArea.purchaseOrderValidation;
  }
  return undefined;
});

export const getPurchaseOrderValidationAndExperience = createSelector(
  getPurchaseOrderValidation,
  getSelectedExperience,
  (purchaseOrderMandatory: boolean, jobsiteExperience: any) => {
    return { purchaseOrderMandatory, jobsiteExperience };
  },
);

export const getPurchaseOrderMaskPatterns = createSelector(getJobsiteState, (state) => {
  return state.purchaseOrderMaskPatterns;
});

export const getStateForPurchaseOrderValidator = createSelector(
  getJobsiteState,
  getBusinessLinePage,
  getPurchaseOrderValidation,
  (jobsite: IJobsiteState, businessLineState: IBusinessLineState, purchaseOrderMandatory: boolean) => {
    return { jobsite, businessLineState, purchaseOrderMandatory };
  },
);

export const getStateForJobsiteLoadExperience = createSelector(
  getJobsiteState,
  getBusinessLinePage,
  getLegalEntity,
  (jobsite: IJobsiteState, businessLineState: IBusinessLineState, customer: ILegalEntity) => {
    return { jobsite, businessLineState, customer };
  },
);
